import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

const ZOOM_FACTOR = 4;

const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value));

/** Shared hook for magnifier mouse tracking */
export function useMagnifierMouse(
  isActive: boolean,
  containerElement: HTMLDivElement | null,
) {
  const [mousePos, setMousePos] = useState<{ x: number; y: number } | null>(
    null,
  );

  useEffect(() => {
    if (!isActive || !containerElement) {
      setMousePos(null);
      return;
    }

    const handleMouseMove = (e: MouseEvent) => {
      const rect = containerElement.getBoundingClientRect();
      const x = clamp((e.clientX - rect.left) / rect.width, 0, 1);
      const y = clamp((e.clientY - rect.top) / rect.height, 0, 1);
      setMousePos({ x, y });
    };

    const handleMouseLeave = () => {
      setMousePos(null);
    };

    containerElement.addEventListener('mousemove', handleMouseMove);
    containerElement.addEventListener('mouseleave', handleMouseLeave);
    return () => {
      containerElement.removeEventListener('mousemove', handleMouseMove);
      containerElement.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [isActive, containerElement]);

  return mousePos;
}

/** Zoomed preview panel — render above the video container in the flex layout */
export const MagnifierPreview: React.FC<{
  videoElement: HTMLVideoElement | null;
  mousePos: { x: number; y: number } | null;
}> = ({ videoElement, mousePos }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const drawZoomedRegion = useCallback(
    (normX: number, normY: number) => {
      const canvas = canvasRef.current;
      if (!canvas || !videoElement || videoElement.readyState < 2) return;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const vw = videoElement.videoWidth;
      const vh = videoElement.videoHeight;
      if (vw === 0 || vh === 0) return;

      const rect = canvas.getBoundingClientRect();
      if (canvas.width !== rect.width || canvas.height !== rect.height) {
        canvas.width = rect.width;
        canvas.height = rect.height;
      }

      const srcW = vw / ZOOM_FACTOR;
      const srcH = vh / ZOOM_FACTOR;
      const srcX = clamp(normX * vw - srcW / 2, 0, vw - srcW);
      const srcY = clamp(normY * vh - srcH / 2, 0, vh - srcH);

      ctx.drawImage(videoElement, srcX, srcY, srcW, srcH, 0, 0, canvas.width, canvas.height);
    },
    [videoElement],
  );

  useEffect(() => {
    if (mousePos) {
      drawZoomedRegion(mousePos.x, mousePos.y);
    }
  }, [mousePos, drawZoomedRegion]);

  if (!mousePos) return null;

  return (
    <PreviewPanel>
      <PreviewCanvas ref={canvasRef} />
    </PreviewPanel>
  );
};

/** Region indicator overlay — render inside VideoPreviewContainer (position: relative) */
export const MagnifierRegionIndicator: React.FC<{
  mousePos: { x: number; y: number } | null;
}> = ({ mousePos }) => {
  if (!mousePos) return null;

  const indicatorW = 100 / ZOOM_FACTOR;
  const indicatorH = 100 / ZOOM_FACTOR;
  const indicatorLeft = clamp(mousePos.x * 100 - indicatorW / 2, 0, 100 - indicatorW);
  const indicatorTop = clamp(mousePos.y * 100 - indicatorH / 2, 0, 100 - indicatorH);

  return (
    <RegionIndicator
      style={{
        left: `${indicatorLeft}%`,
        top: `${indicatorTop}%`,
        width: `${indicatorW}%`,
        height: `${indicatorH}%`,
      }}
    />
  );
};

const PreviewPanel = styled.div`
  width: 100%;
  aspect-ratio: 16 / 9;
  flex-shrink: 0;
  background: #000;
  border-top: 1px solid #484848;
  border-bottom: 1px solid #484848;
`;

const PreviewCanvas = styled.canvas`
  width: 100%;
  height: 100%;
  display: block;
`;

const RegionIndicator = styled.div`
  position: absolute;
  border: 2px solid rgba(255, 255, 255, 0.7);
  border-radius: 2px;
  pointer-events: none;
  z-index: 3;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.3);
`;
